import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './Button';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Market & Products', path: '/products' },
    { name: 'Machinery & Technology', path: '/technology' },
    { name: 'SSE', path: 'http://shrishyamenterprise.net/', target: '_blank', rel: 'noopener noreferrer' },
  ];

  const isActive = (path: string) => {
    if (path === '/' && location.pathname !== '/') return false;
    return location.pathname.startsWith(path);
  };

  return (
    <motion.nav 
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: "easeOut", delay: 2.4 }}
      className="fixed top-2 md:top-6 lg:top-8 left-0 right-0 z-50 flex justify-center px-3 md:px-4"
    >
      <motion.div 
        layout
        className={`w-full max-w-[1277px] bg-[#F0E4EC] rounded-2xl flex flex-col items-center justify-between px-4 md:px-8 transition-all duration-300 relative ${isMobileMenuOpen ? 'rounded-[20px]' : ''}`}
        style={{ 
          height: isMobileMenuOpen ? 'auto' : (isScrolled ? '70px' : '80px'),
          boxShadow: isScrolled || isMobileMenuOpen ? '0 10px 30px -5px rgba(0, 0, 0, 0.1)' : 'none'
        }}
      >
        <div className="w-full h-[60px] md:h-full flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group cursor-target"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <motion.img 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              src="/Images/PrintAura.webp" 
              alt="PrintAura Icon" 
              className="h-7 md:h-9 object-contain"
            />
            
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-[40px]">
            {navLinks.map((link) => {
              const active = isActive(link.path);
              return (
                <Link
                  key={link.name}
                  to={link.path}
                  className="cursor-target relative group"
                  target={link.target}
                  rel={link.rel}
                >
                  <span 
                    className={`font-extrabold transition-colors ${active ? 'text-primary' : 'text-text-main group-hover:text-primary'}`}
                    style={{
                      fontSize: '16px',
                      lineHeight: '100%',
                      letterSpacing: '0%'
                    }}
                  >
                    {link.name}
                  </span>
                  <span className={`absolute -bottom-1 left-0 h-0.5 bg-primary transition-all duration-300 ${active ? 'w-full' : 'w-0 group-hover:w-full'}`} />
                </Link>
              );
            })}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link to="/contact">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  className={`text-white px-6 rounded-full font-extrabold shadow-md hover:shadow-lg transition-all ${isActive('/contact') ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                  style={{
                    background: 'linear-gradient(88.69deg, #CA1167 -1.27%, #F47749 98.89%)'
                  }}
                >
                  Contact us
                </Button>
              </motion.div>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            className="cursor-target lg:hidden text-text-main p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </motion.button>
        </div>

        {/* Mobile Menu Content */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="w-full overflow-hidden lg:hidden flex flex-col gap-4 pb-6 pt-2 border-t border-gray-200/50"
            >
              {navLinks.map((link, index) => {
                const active = isActive(link.path);
                return (
                  <motion.div
                    key={link.name}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={link.path}
                      className={`cursor-target block font-extrabold text-lg py-2 pl-2 border-l-4 transition-all rounded-r-lg ${
                        active 
                          ? 'text-white bg-primary border-primary' 
                          : 'text-text-main hover:text-primary hover:bg-white border-transparent'
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                      target={link.target}
                      rel={link.rel}
                    >
                      {link.name}
                    </Link>
                  </motion.div>
                );
              })}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mt-2"
              >
                <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button 
                    className={`w-full text-white py-3 rounded-xl font-extrabold shadow-md ${isActive('/contact') ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                    style={{
                      background: 'linear-gradient(88.69deg, #CA1167 -1.27%, #F47749 98.89%)'
                    }}
                  >
                    Contact us
                  </Button>
                </Link>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.nav>
  );
};
